import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, getIdentity, setIdentity as persistIdentity, clearIdentity } from "./api";
import { clearActiveCheckIn, getActiveCheckIn } from "./activeCheckIn";
import { useAuth } from "./auth";
import { isDemoDancerId } from "./demoExperience";

const GuestIdentityCtx = createContext(null);
export const useGuestIdentity = () => useContext(GuestIdentityCtx);

function readStoredIdentity() {
  const ident = getIdentity();
  return ident?.dancer_id ? ident : null;
}

function profileFromApi(data) {
  const id = data.id || data.dancer_id;
  if (!id) return null;
  return {
    id,
    phone: data.phone,
    name: data.name,
    role: data.role,
    dance_forms: data.dance_forms || [],
    mini_profile: data.mini_profile,
    show_mini_on_events: data.show_mini_on_events === true,
    thumbnail_url: data.thumbnail_url ?? null,
  };
}

function identityFromProfile(profile) {
  if (!profile?.id) return null;
  return {
    dancer_id: profile.id,
    name: profile.name,
    role: profile.role,
    dance_forms: profile.dance_forms || [],
  };
}

export function GuestIdentityProvider({ children }) {
  const { account } = useAuth();
  const hadOrganizerRef = useRef(!!account);
  const [identity, setIdentityState] = useState(() => readStoredIdentity());
  const [profile, setProfile] = useState(null);
  const [revision, setRevision] = useState(0);

  const applyProfile = useCallback((data) => {
    const nextProfile = profileFromApi(data);
    const nextIdentity = identityFromProfile(nextProfile);
    if (nextIdentity) persistIdentity(nextIdentity);
    setIdentityState(nextIdentity);
    setProfile(nextProfile);
    setRevision((r) => r + 1);
    return nextProfile;
  }, []);

  const clearGuest = useCallback(() => {
    clearActiveCheckIn();
    clearIdentity();
    setIdentityState(null);
    setProfile(null);
    setRevision((r) => r + 1);
  }, []);

  // Drop cached guest/dancer session when the account account signs out.
  useEffect(() => {
    const hadOrganizer = hadOrganizerRef.current;
    const hasOrganizer = !!account;
    if (hadOrganizer && !hasOrganizer) {
      clearGuest();
    }
    hadOrganizerRef.current = hasOrganizer;
  }, [account, clearGuest]);

  const bindDancerId = useCallback((nextDancerId) => {
    if (!nextDancerId) return;
    const current = getActiveCheckIn();
    if (current?.dancer_id && current.dancer_id !== nextDancerId) {
      clearActiveCheckIn();
    }
    const next = { dancer_id: nextDancerId };
    persistIdentity(next);
    setIdentityState((prev) => ({ ...prev, ...next }));
    setProfile((prev) => (prev?.id === nextDancerId ? prev : null));
    setRevision((r) => r + 1);
  }, []);

  const refreshProfile = useCallback(async () => {
    const dancerId = identity?.dancer_id;
    if (!dancerId) {
      setProfile(null);
      setRevision((r) => r + 1);
      return null;
    }
    if (isDemoDancerId(dancerId)) {
      setProfile(null);
      setRevision((r) => r + 1);
      return null;
    }
    const r = await api.get(`/dancers/${dancerId}`);
    return applyProfile(r.data);
  }, [identity?.dancer_id, applyProfile]);

  useEffect(() => {
    const id = identity?.dancer_id;
    if (!id) {
      setProfile(null);
      return;
    }
    if (isDemoDancerId(id)) {
      setProfile(null);
      return;
    }
    if (profile?.id === id) return;

    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/dancers/${id}`);
        if (!cancelled) applyProfile(r.data);
      } catch {
        if (!cancelled) setProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identity?.dancer_id, profile?.id, applyProfile]);

  const dancerId = identity?.dancer_id || null;

  const value = {
    identity,
    profile,
    dancerId,
    revision,
    bindDancerId,
    clearGuest,
    refreshProfile,
  };

  return <GuestIdentityCtx.Provider value={value}>{children}</GuestIdentityCtx.Provider>;
}
